import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import {
  POS_PAYMENT_METHODS_DEFAULT,
  makePosPaymentMethod,
  type PosPaymentMethod,
} from '@mlm/shared';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mlm/ui';
import { getTenantFeatures, patchTenantFeatures } from '../lib/api';
import { hasRole } from '../lib/auth-store';
import { ConfirmSaveButton } from '../components/confirm-save-button';

/**
 * POS (Cashier) payment methods — admin-managed registry for the in-person
 * tender picker. Each row pairs a `tenderKind` (cash-with-change | exact | wallet)
 * with a wallet `currency` (for wallet methods). super_admin only. Mirrors the
 * typed-editor pattern in `ecom-payment-methods.tsx` / `payout-deductions.tsx`.
 */
function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

export function PosPaymentMethodsPage() {
  const qc = useQueryClient();
  const canWrite = hasRole('super_admin');
  const q = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });

  const [methods, setMethods] = useState<PosPaymentMethod[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!q.data) return;
    const cfg = q.data.pos_payment_methods ?? POS_PAYMENT_METHODS_DEFAULT;
    setMethods(Array.isArray(cfg.methods) ? clone(cfg.methods) : []);
  }, [q.data]);

  const save = useMutation({
    meta: { successMessage: 'POS payment methods saved.' },
    mutationFn: () => patchTenantFeatures({ pos_payment_methods: { methods } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-features'] }),
  });

  function updateMethod(i: number, patch: Partial<PosPaymentMethod>) {
    setMethods((ms) => ms.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  }
  function addMethod() {
    setMethods((ms) => [...ms, makePosPaymentMethod()]);
  }
  function removeMethod(i: number) {
    setMethods((ms) => ms.filter((_, idx) => idx !== i));
  }

  function validate(): string | null {
    const codes = methods.map((m) => m.code.trim());
    let msg: string | null = null;
    if (codes.some((c) => c === '')) msg = 'Every method needs a code.';
    else if (codes.some((c) => !/^[a-z0-9_]+$/.test(c)))
      msg = 'Codes must be lowercase letters, digits, or underscores.';
    else if (new Set(codes).size !== codes.length) msg = 'Method codes must be unique.';
    else if (methods.some((m) => m.label.trim() === '')) msg = 'Every method needs a label.';
    // Cash/exact tenders are always cash-denominated; only a wallet tender may
    // debit a non-cash (GC/Coin) balance. A wallet method may debit the CASH
    // wallet too — the buyer pays the normal cash total from their cash balance.
    else if (methods.some((m) => m.tenderKind !== 'wallet' && m.currency !== 'cash'))
      msg = 'Only wallet methods can use a GC/Coin currency — set cash/exact methods to Cash.';
    else if (!methods.some((m) => m.enabled))
      msg = 'Enable at least one method — the cashier needs a way to take payment.';
    setErr(msg);
    return msg;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">POS payment methods</h1>
        <p className="text-sm text-muted-foreground">
          The tender options the cashier picks at checkout. <b>Cash</b> takes an amount tendered and
          gives change; <b>Exact</b> requires the tendered amount to equal the total (e.g. a card
          swipe, no change); <b>Wallet</b> debits the buyer slot's balance in the chosen currency —
          Cash (pays the normal cash total from the member's cash wallet), GC, or Coin. Each sale
          records the method for the daily-sales breakdown.
        </p>
        {!canWrite && (
          <p className="text-xs text-muted-foreground mt-1">
            Read-only. Payment methods are edited by a super-admin.
          </p>
        )}
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          {methods.length === 0 && (
            <Card>
              <CardContent className="py-6 text-sm text-muted-foreground">
                No payment methods yet. {canWrite ? 'Add one below.' : 'A super-admin can add them.'}{' '}
                The cashier can't take payment until at least one method is enabled.
              </CardContent>
            </Card>
          )}

          {methods.map((m, i) => (
            <MethodCard
              key={i}
              method={m}
              canWrite={canWrite}
              onChange={(patch) => updateMethod(i, patch)}
              onRemove={() => removeMethod(i)}
            />
          ))}

          {canWrite && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={addMethod}>
                <Plus className="h-4 w-4" /> Add method
              </Button>
              <ConfirmSaveButton
                size="sm"
                label="Save"
                title="Save POS payment methods"
                current={q.data?.pos_payment_methods}
                next={{ methods }}
                loading={save.isPending}
                validate={validate}
                onInvalid={setErr}
                onConfirm={() => save.mutateAsync()}
              />
              {save.isSuccess && !save.isPending && (
                <span className="text-xs text-success">Saved.</span>
              )}
              {err && <span className="text-xs text-destructive">{err}</span>}
              {save.error && (
                <span className="text-xs text-destructive">Save failed. Try again.</span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MethodCard({
  method: m,
  canWrite,
  onChange,
  onRemove,
}: {
  method: PosPaymentMethod;
  canWrite: boolean;
  onChange: (patch: Partial<PosPaymentMethod>) => void;
  onRemove: () => void;
}) {
  const isWallet = m.tenderKind === 'wallet';
  return (
    <Card>
      <CardContent className="py-4 space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Code</label>
            <Input
              value={m.code}
              disabled={!canWrite}
              placeholder="gcash"
              onChange={(e) => onChange({ code: e.target.value.trim() })}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Label</label>
            <Input
              value={m.label}
              disabled={!canWrite}
              placeholder="GCash"
              onChange={(e) => onChange({ label: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Tender</label>
            <Select
              value={m.tenderKind}
              disabled={!canWrite}
              onValueChange={(v) => {
                const tenderKind = v as PosPaymentMethod['tenderKind'];
                // A cash/exact tender is always cash. A wallet tender keeps its
                // currency (cash / gc / coin) — cash-wallet debits the cash balance.
                onChange({
                  tenderKind,
                  currency: tenderKind === 'wallet' ? m.currency : 'cash',
                });
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash (with change)</SelectItem>
                <SelectItem value="exact">Exact (no change)</SelectItem>
                <SelectItem value="wallet">Wallet debit</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Currency</label>
            <Select
              value={m.currency}
              disabled={!canWrite || !isWallet}
              onValueChange={(v) => onChange({ currency: v as PosPaymentMethod['currency'] })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="gc">GC</SelectItem>
                <SelectItem value="coin">Coin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={m.enabled}
              disabled={!canWrite}
              onCheckedChange={(v) => onChange({ enabled: v === true })}
            />
            Enabled
          </label>
          {canWrite && (
            <Button variant="ghost" size="sm" onClick={onRemove}>
              <Trash2 className="h-4 w-4" /> Remove
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
